import { useEffect, useState } from "react";
import RightModal from "../components/templates/RightModal";
import { getInternalVehiclesList, selectInternalVehiclesData, selectInternalVehicleLoading, deleteInternalVehicle } from "../redux/internalVehicle/internalVehicleSlice";
import { toast } from 'react-toastify';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import DynamicIcon from "../components/DynamicIcon";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import AddEditInternalVehicle from "../components/internalVehiclePage/AddEditInternalVehicle";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import { getNodeApiUrl } from "../config/api.config";
import { useColorPalette } from '../hooks/useColorPalette';
import DataTable from "../components/DataTable/DataTable";

export default function InternalVehiclesList() {
    const dispatch = useDispatch();
    const palette = useColorPalette();
    const internalVehiclesList = useSelector(selectInternalVehiclesData);
    const internalVehiclesLoading = useSelector(selectInternalVehicleLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [searchTerm, setSearchTerm] = useState('');
    const [dataTableKey, setDataTableKey] = useState(Date.now());
    
    // Stato per la modale di conferma eliminazione
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [vehicleToDelete, setVehicleToDelete] = useState(null);
    
    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalVehicleId = useSelector(selectRightModalId);
    
    // Gestione della paginazione con DataTable
    const handlePageChange = (pageIndex, pageSize) => {
        console.log('Cambio pagina a:', pageIndex, 'dimensione:', pageSize);
        const start = pageIndex * pageSize;
        
        dispatch(getInternalVehiclesList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            start,
            length: pageSize,
            order: [{ column: 'model', dir: 'asc' }] // Ordinamento alfabetico per modello
        }));
        dispatch(resetRefreshNow());
    };
    
    // Gestione della ricerca
    const handleSearch = (searchValue) => {
        console.log('Ricerca:', searchValue);
        setSearchTerm(searchValue);
        
        if (searchValue.length >= 3 || searchValue.length === 0) {
            dispatch(getInternalVehiclesList({
                accountId: accountData?._id,
                token,
                search: searchValue.length >= 3 ? searchValue : '',
                start: 0,
                length: 10,
                order: [{ column: 'model', dir: 'asc' }] // Ordinamento alfabetico per modello
            }));
            dispatch(resetRefreshNow());
        }
    };

    // Eseguo la chiamata iniziale quando il componente viene montato
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getInternalVehiclesList({
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 3 ? searchTerm : '',
                start: 0, // Inizio da zero
                length: 10, // Uso la dimensione della pagina predefinita
                order: [{ column: 'model', dir: 'asc' }] // Ordinamento alfabetico per modello
            }));
            dispatch(resetRefreshNow());
        }
    }, [dispatch, accountData, searchTerm]);
    
    // Avvio il refresh dei dati quando isRefreshNow della RightModal si attiva
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow) {
            handlePageUpdate(0, 10);
            setDataTableKey(Date.now()); // Forza il re-render della tabella
        }
    }, [rightModalIsRefreshNow]);
    
    // Aggiorna la tabella quando il modal viene chiuso
    useEffect(() => {
        if (!rightModalIsOpen && dataTableKey) {
            setDataTableKey(Date.now());
        }
    }, [rightModalIsOpen]);
    
    // Funzione per gestire l'eliminazione di un veicolo
    const handleDeleteClick = (vehicle) => {
        setVehicleToDelete(vehicle);
        setShowDeleteModal(true);
    };
    
    const confirmDelete = async () => {
        if (vehicleToDelete) {
            try {
                await dispatch(deleteInternalVehicle({ 
                    vehicleId: vehicleToDelete._id || vehicleToDelete.id, 
                    token 
                })).unwrap();
                toast.success('Veicolo eliminato con successo');
                setDataTableKey(Date.now());
                // Ricarica la lista
                dispatch(getInternalVehiclesList({
                    accountId: accountData?._id,
                    token,
                    search: searchTerm.length >= 3 ? searchTerm : '',
                    start: 0,
                    length: 10,
                    order: [{ column: 'model', dir: 'asc' }]
                }));
            } catch (error) {
                toast.error('Errore nell\'eliminazione del veicolo');
            }
        }
        setShowDeleteModal(false);
        setVehicleToDelete(null);
    };
    
    const cancelDelete = () => {
        setShowDeleteModal(false);
        setVehicleToDelete(null);
    };
    
    // Funzione per aggiornare la pagina
    const handlePageUpdate = (start, length) => {
        dispatch(getInternalVehiclesList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            start,
            length,
            order: [{ column: 'model', dir: 'asc' }]
        }));
    };
    

    
    return (
        <div className="page">
            {internalVehiclesLoading && <LoadingSection isFixed />}
            <h1><span className="font-light">Veicoli / </span>Lista Veicoli Interni</h1>
            <div className="box">
                {/* Nuovo veicolo */}
                <div className="flex justify-end items-center gap-2 mb-4">
                    <button 
                        className="primary" 
                        onClick={() => dispatch(setModal({ mode: 'add', percentuageWidth: 30 }))}
                    >
                        <DynamicIcon iconClass="fa-solid fa-plus" />
                        Nuovo Veicolo Interno
                    </button>
                </div>

                {/* DataTable per i veicoli interni */}
                <DataTable
                    key={dataTableKey}
                    columns={[
                        { 
                            id: 'model', 
                            title: 'MODELLO',
                            width: '30%',
                            render: (vehicle) => (
                                <div className="text-left uppercase">
                                    {vehicle.model || '-'}
                                </div>
                            )
                        },
                        { 
                            id: 'plateNumber', 
                            title: 'TARGA',
                            width: '25%',
                            render: (vehicle) => (
                                <div className="text-left uppercase">
                                    {vehicle.plateNumber || '-'}
                                </div>
                            )
                        },
                        { 
                            id: 'km', 
                            title: 'KM',
                            width: '20%',
                            render: (vehicle) => (
                                <div className="text-left">
                                    {vehicle.km !== null && vehicle.km !== undefined ? parseInt(vehicle.km, 10).toLocaleString('it-IT') : '0'}
                                </div>
                            )
                        },
                        { 
                            id: 'actions', 
                            title: 'AZIONI',
                            width: '25%',
                            render: (vehicle) => (
                                <div className="flex gap-3 justify-center">
                                    <button 
                                        style={{ color: 'inherit' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            dispatch(setModal({
                                                mode: 'edit',
                                                id: vehicle._id || vehicle.id,
                                                percentuageWidth: 30
                                            }));
                                        }}
                                    >
                                        <DynamicIcon iconClass="fa-solid fa-edit" />
                                    </button>
                                    <button 
                                        style={{ color: 'inherit' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteClick(vehicle);
                                        }}
                                    >
                                        <DynamicIcon iconClass="fa-solid fa-trash" />
                                    </button>
                                </div>
                            )
                        }
                    ]}
                    data={internalVehiclesList?.data || []}
                    loading={internalVehiclesLoading}
                    totalRecords={internalVehiclesList?.recordsFiltered || internalVehiclesList?.data?.length || 0}
                    pageSize={10}
                    onPageChange={handlePageChange}
                    hasSearch={true}
                    onSearch={handleSearch}
                    initialSearchTerm={searchTerm}
                    emptyMessage="Nessun veicolo interno trovato"
                    renderCustomRow={(vehicle, index, isEven, tableStyles) => {
                        // Funzione per gestire il click sulla riga - apre in edit
                        const handleRowClick = () => {
                            dispatch(setModal({
                                mode: 'edit',
                                id: vehicle._id || vehicle.id,
                                percentuageWidth: 30
                            }));
                        };
                        
                        return (
                            <div 
                                key={vehicle._id || vehicle.id}
                                className="data-table-row cursor-pointer"
                                style={{
                                    backgroundColor: isEven ? tableStyles.evenRow.backgroundColor : tableStyles.oddRow.backgroundColor,
                                    color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color,
                                }}
                                onClick={handleRowClick}
                            >
                                {/* Colonna MODELLO */}
                                <div className="data-table-cell" style={{ width: '30%' }} data-label="MODELLO">
                                    <div className="text-left uppercase">
                                        {vehicle.model || '-'}
                                    </div>
                                </div>
                                
                                {/* Colonna TARGA */}
                                <div className="data-table-cell" style={{ width: '25%' }} data-label="TARGA">
                                    <div className="text-left uppercase">
                                        {vehicle.plateNumber || '-'}
                                    </div>
                                </div>
                                
                                {/* Colonna KM */}
                                <div className="data-table-cell" style={{ width: '20%' }} data-label="KM">
                                    <div className="text-left">
                                        {vehicle.km !== null && vehicle.km !== undefined ? parseInt(vehicle.km, 10).toLocaleString('it-IT') : '0'}
                                    </div>
                                </div>
                                
                                {/* Colonna AZIONI */}
                                <div className="data-table-cell" style={{ width: '25%' }} data-label="AZIONI">
                                    <div className="flex gap-3 justify-center">
                                        <button 
                                            style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(setModal({
                                                    mode: 'edit',
                                                    id: vehicle._id || vehicle.id,
                                                    percentuageWidth: 30
                                                }));
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-edit" />
                                        </button>
                                        <button 
                                            style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteClick(vehicle);
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-trash" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                />
            </div>

            {/* RIGHT MODAL per inserimento o modifica veicolo interno */}
            {
                rightModalIsOpen && (rightModalMode === 'add' || rightModalMode === 'edit') && (
                    <RightModal>
                        <AddEditInternalVehicle vehicleId={rightModalVehicleId} />
                    </RightModal>
                )
            }
            
            {/* Modale di conferma eliminazione */}
            <Modal isOpen={showDeleteModal} toggle={cancelDelete}>
                <ModalHeader toggle={cancelDelete}>Conferma Eliminazione</ModalHeader>
                <ModalBody>
                    <p>Sei sicuro di voler eliminare il veicolo <strong>{vehicleToDelete?.model}</strong> ({vehicleToDelete?.plateNumber})?</p>
                    <p className="text-danger">Questa azione non può essere annullata.</p>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={cancelDelete}>Annulla</Button>
                    <Button color="danger" onClick={confirmDelete}>Elimina</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
